import { ref } from 'vue'
import { defineStore } from 'pinia'

/**
 * Demandes de confirmation.
 *
 * Pourquoi ne pas garder `window.confirm()`, qui tient en une ligne :
 *
 *   * un navigateur qui a « bloqué les boîtes de dialogue de cette page » —
 *     l'utilisateur a coché la case, ou une extension l'a fait — renvoie
 *     `false` sans rien afficher. La suppression ne se fait alors **jamais, en
 *     silence**, et personne ne comprend pourquoi ;
 *   * l'appel BLOQUE le fil d'exécution : les animations se figent, les
 *     rafraîchissements en cours s'arrêtent ;
 *   * il ne se style pas, ne se traduit pas, et affiche l'URL du site au-dessus
 *     du message.
 *
 * Un store plutôt qu'un `provide/inject` : n'importe quel module — y compris
 * du code hors composant — doit pouvoir demander une confirmation, comme pour
 * les notifications.
 */

export interface DemandeConfirmation {
  title: string
  message: string
  /** Libellé du bouton d'action. Par défaut : « Confirmer ». */
  confirmLabel?: string
  cancelLabel?: string
  /** Action destructrice : le bouton passe en rouge. */
  danger?: boolean
}

interface EnAttente extends DemandeConfirmation {
  resolve: (accepte: boolean) => void
}

export const useConfirmStore = defineStore('confirm', () => {
  /** Demande actuellement affichée. */
  const courante = ref<EnAttente | null>(null)
  /**
   * File des demandes suivantes.
   *
   * Deux confirmations simultanées arrivent pour de vrai : un raccourci clavier
   * déclenché deux fois, une action de masse lancée pendant qu'une autre attend.
   * Sans file, la seconde écraserait la première — dont la promesse ne se
   * résoudrait JAMAIS, laissant l'appelant bloqué sur son `await` pour la durée
   * de vie de l'onglet.
   */
  const file = ref<EnAttente[]>([])

  /**
   * Demande une confirmation. Retourne `true` si l'utilisateur accepte.
   *
   * La promesse se résout TOUJOURS — accepter, refuser, fermer par Échap ou par
   * le fond donnent `false`. Un chemin qui ne résout pas laisserait l'appelant
   * en attente définitive.
   */
  function ask(demande: DemandeConfirmation): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const entree: EnAttente = { ...demande, resolve }
      if (courante.value === null) courante.value = entree
      else file.value.push(entree)
    })
  }

  function repond(accepte: boolean): void {
    const entree = courante.value
    courante.value = file.value.shift() ?? null
    // La résolution vient APRÈS le changement d'état : si l'appelant relance une
    // confirmation dans la continuation de sa promesse, elle s'empile
    // proprement au lieu d'être écrasée par la remise à zéro.
    entree?.resolve(accepte)
  }

  const confirm = () => repond(true)
  const cancel = () => repond(false)

  return { courante, file, ask, confirm, cancel }
})
